import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Collapse,
  IconButton,
  Paper,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';

export const RouteErrorBoundary = () => {
  const error = useRouteError();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const getErrorMessage = () => {
    if (isRouteErrorResponse(error)) return `${error.status} - ${error.statusText}`;
    if (error instanceof Error) return error.message;
    return 'An unexpected error occurred';
  };

  const getErrorStack = () => {
    if (error instanceof Error) return error.stack || 'No stack trace available';
    return JSON.stringify(error, null, 2);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`Error: ${getErrorMessage()}\n\n${getErrorStack()}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
        <ErrorIcon sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Something went wrong
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {getErrorMessage()}
        </Typography>

        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 3 }}
        >
          Go Back
        </Button>

        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
          <Button
            size="small"
            color="inherit"
            onClick={() => setIsExpanded(!isExpanded)}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            }
            sx={{ color: 'text.secondary' }}
          >
            {isExpanded ? 'Hide details' : 'Show details'}
          </Button>

          <Collapse in={isExpanded}>
            <Box sx={{ mt: 2, textAlign: 'left' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <IconButton size="small" onClick={handleCopy} color={copied ? 'success' : 'default'}>
                  {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                </IconButton>
              </Box>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: 'grey.900',
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    m: 0,
                    fontSize: 11,
                    fontFamily: 'monospace',
                    color: 'error.light',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {getErrorStack()}
                </Typography>
              </Paper>
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
};
